import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Package, Edit2, Check, X, Search, Settings, Save } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

const DEFAULT_LOW_STOCK_THRESHOLD = 10;

const AdminLowStockPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalThreshold, setGlobalThreshold] = useState(DEFAULT_LOW_STOCK_THRESHOLD);
  const [editingStock, setEditingStock] = useState(null);
  const [newStockValue, setNewStockValue] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Redirect non-admins
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/");
    }
  }, [user, navigate]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products?limit=500`);
      setProducts(response.data?.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // Load global threshold from localStorage
    const savedThreshold = localStorage.getItem("lowStockThreshold");
    if (savedThreshold) {
      setGlobalThreshold(parseInt(savedThreshold, 10));
    }
  }, [token]);

  const getThreshold = (product) => {
    return product.low_stock_threshold || globalThreshold;
  };

  const lowStockProducts = products.filter(p => p.stock <= getThreshold(p));

  const filteredProducts = lowStockProducts.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const updateStock = async (productId, newStock) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/products/${productId}`,
        { stock: parseInt(newStock, 10) },
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Stock updated successfully!");
      setEditingStock(null);
      fetchProducts();
    } catch (error) {
      console.error("Error updating stock:", error);
      toast.error("Failed to update stock");
    } finally {
      setSaving(false);
    }
  };

  const markOutOfStock = async (productId) => {
    await updateStock(productId, 0);
  };

  const saveGlobalThreshold = () => {
    localStorage.setItem("lowStockThreshold", globalThreshold.toString());
    toast.success("Global threshold saved");
    setSettingsOpen(false);
  };

  const startEditingStock = (product) => {
    setEditingStock(product.product_id);
    setNewStockValue(product.stock.toString());
  };

  const cancelEditing = () => {
    setEditingStock(null);
    setNewStockValue("");
  };

  const getStockStatus = (product) => {
    const threshold = getThreshold(product);
    if (product.stock === 0) {
      return { label: "Out of Stock", color: "bg-red-100 text-red-800" };
    } else if (product.stock <= threshold / 2) {
      return { label: "Critical", color: "bg-red-100 text-red-800" };
    } else {
      return { label: "Low", color: "bg-amber-100 text-amber-800" };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-low-stock-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="container mx-auto px-4">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 uppercase tracking-tight">
                  Low Stock Alert
                </h1>
                <p className="text-gray-500 text-sm">{lowStockProducts.length} products below threshold</p>
              </div>
            </div>
            <Button onClick={() => setSettingsOpen(true)} variant="outline">
              <Settings className="w-4 h-4 mr-2" />
              Threshold Settings
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by product name, SKU, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading inventory...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">All products are well stocked!</p>
              <p className="text-gray-400 text-sm">No products below the threshold of {globalThreshold} units</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="low-stock-table">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Product</th>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">SKU</th>
                    <th className="text-center p-4 font-medium text-gray-600 text-sm">Current Stock</th>
                    <th className="text-center p-4 font-medium text-gray-600 text-sm">Threshold</th>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Category</th>
                    <th className="text-left p-4 font-medium text-gray-600 text-sm">Position</th>
                    <th className="text-center p-4 font-medium text-gray-600 text-sm">Status</th>
                    <th className="text-center p-4 font-medium text-gray-600 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => {
                    const status = getStockStatus(product);
                    return (
                      <tr
                        key={product.product_id}
                        className="border-b border-gray-100 hover:bg-gray-50"
                        data-testid={`stock-row-${product.product_id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={product.images?.[0] || "https://via.placeholder.com/48"}
                              alt={product.name}
                              className="w-12 h-12 object-cover rounded"
                            />
                            <div>
                              <p className="font-medium text-gray-900">{product.name}</p>
                              <p className="text-xs text-gray-500">{product.brand}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 font-mono text-sm text-gray-600">{product.sku}</td>
                        <td className="p-4 text-center">
                          {editingStock === product.product_id ? (
                            <div className="flex items-center justify-center gap-2">
                              <Input
                                type="number"
                                value={newStockValue}
                                onChange={(e) => setNewStockValue(e.target.value)}
                                className="w-20 text-center"
                                min="0"
                                autoFocus
                              />
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600"
                                onClick={() => updateStock(product.product_id, newStockValue)}
                                disabled={saving}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600"
                                onClick={cancelEditing}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <span className={`font-bold text-lg ${product.stock === 0 ? "text-red-600" : product.stock <= getThreshold(product) / 2 ? "text-amber-600" : "text-gray-900"}`}>
                              {product.stock}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-center text-gray-500">{getThreshold(product)}</td>
                        <td className="p-4 text-sm text-gray-600 capitalize">{product.category}</td>
                        <td className="p-4 text-sm text-gray-600">
                          {product.position && <span className="capitalize">{product.position}</span>}
                          {product.position && product.side && " / "}
                          {product.side && <span>{product.side}</span>}
                          {!product.position && !product.side && "-"}
                        </td>
                        <td className="p-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditingStock(product)}
                              title="Edit stock"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {product.stock > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => markOutOfStock(product.product_id)}
                                title="Mark as out of stock"
                              >
                                Out of Stock
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Threshold Settings Dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Low Stock Threshold Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                Global Low Stock Threshold
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  value={globalThreshold}
                  onChange={(e) => setGlobalThreshold(parseInt(e.target.value, 10) || 10)}
                  className="w-32"
                  min="1"
                />
                <span className="text-gray-500">units</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Products with stock at or below this number will appear in the Low Stock list.
              </p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Tip:</strong> You can set per-product thresholds in the Product Edit form for items that need different stock levels.
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveGlobalThreshold}>
                <Save className="w-4 h-4 mr-2" />
                Save Threshold
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLowStockPage;
